/**
 * StructurePanel — M7 human structural editor.
 *
 * Tier A (iter-19): per-block role dropdown, autosave, evidence display,
 * expand/collapse groups, restore per-block, reset-all.
 * Tier B (iter-20): sticky role tray with drag-drop, no re-fetch after
 * PATCH (touched_block delta), shadcn AlertDialog on Reset all.
 * Tier C (iter-23): multi-select + bulk apply, split/merge chapter
 * shortcuts. Selection uses standard shift-click range + ctrl/cmd-click
 * toggle patterns; bulk apply hits the single-pass batch endpoint so 100
 * role changes only cost one HTML re-render.
 *
 * Backend contract:
 *   GET   /api/books/:id/structure               → { blocks, ... }
 *   PATCH /api/books/:id/structure/:sourceIndex  → { touched_block, ... }
 *   PATCH /api/books/:id/structure/roles         → { applied, book, ... }
 *   POST  /api/books/:id/structure/reset         → { book, overrides:{} }
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, RotateCcw, Wand2, AlertCircle, GripVertical,
  CheckSquare, Square, SplitSquareVertical, Combine, XCircle, Lock, Unlock,
  Trash2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Human labels for backend _EDITOR_ROLE_CHOICES. Keep in sync.
const ROLE_LABELS = {
  chapter_title: "Chapter title",
  chapter_subtitle: "Chapter subtitle",
  section_heading: "Section heading",
  subsection_heading: "Subsection heading",
  minor_heading: "Minor heading",
  body_paragraph: "Body paragraph",
  block_quote: "Block quote",
  callout: "Callout",
  epigraph: "Epigraph",
  exercise: "Exercise",
  worksheet_prompt: "Worksheet prompt",
  reflection_question: "Reflection question",
  bullet_item: "Bullet",
  numbered_item: "Numbered",
  caption: "Caption",
};
function fmtRole(r) { return r ? (ROLE_LABELS[r] || r) : "—"; }
function trim(text, n = 140) {
  if (!text) return "";
  const t = text.replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

// Custom mime-type used for HTML5 DnD payload — carrying just the source
// index (integer, as string).
const DND_MIME = "application/x-bookforge-block";

function groupBlocks(blocks) {
  const groups = new Map();
  for (const b of blocks) {
    const label = b.owner_label || (b.owner_kind === "unresolved" ? "⚠ Unresolved" : "(unassigned)");
    const key = `${b.owner_kind || "x"}::${label}`;
    if (!groups.has(key)) {
      groups.set(key, { key, label, kind: b.owner_kind || "unknown", blocks: [] });
    }
    groups.get(key).blocks.push(b);
  }
  const arr = [...groups.values()];
  arr.sort((a, b) => (a.blocks[0]?.source_index ?? 0) - (b.blocks[0]?.source_index ?? 0));
  return arr;
}

export function StructurePanel({ bookId, onBookUpdated, history }) {
  const [state, setState] = useState({
    loading: true, has_structure: false, blocks: [],
    role_choices: [], overrides: {}, report: null,
  });
  const [expanded, setExpanded] = useState({});
  const [savingIdx, setSavingIdx] = useState(() => new Set());
  const [expandedList, setExpandedList] = useState(false);
  const [dragging, setDragging] = useState(null); // {source_index}
  const [hoverRole, setHoverRole] = useState(null);
  // Tier C — selection state.
  //   ``selected`` is a Set of source_index integers.
  //   ``lastAnchor`` is the source_index of the last non-shift click,
  //   used as the range anchor for shift-click.
  const [selected, setSelected] = useState(() => new Set());
  const lastAnchorRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const autoExpandRef = useRef(new Set());

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/books/${bookId}/structure`);
      setState({ loading: false, ...data });
    } catch (err) {
      toast.error(formatApiError(err));
      setState((s) => ({ ...s, loading: false }));
    }
  }, [bookId]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => groupBlocks(state.blocks || []), [state.blocks]);

  /**
   * Apply the response of PATCH /structure/:i in-place. This replaces the
   * old full re-fetch. If ``touched_block`` is missing, fall back to the
   * optimistic value we already applied.
   */
  const applyPatchResponse = useCallback((idx, data) => {
    setState((prev) => {
      const nextBlocks = prev.blocks.map((b) => {
        if (b.source_index !== idx) return b;
        return data.touched_block ? { ...b, ...data.touched_block } : b;
      });
      return {
        ...prev,
        blocks: nextBlocks,
        overrides: data.overrides || prev.overrides,
        report: data.report || prev.report,
      };
    });
  }, []);

  const setBlockRole = useCallback(async (block, nextRole) => {
    const idx = block.source_index;
    const prevBlocks = state.blocks;
    // iter-39: capture the pre-mutation snapshot BEFORE the PATCH so
    // undo can restore the old role/override map.
    history?.record?.("structure_role",
      nextRole ? `Block #${idx}: ${fmtRole(block.effective_role)} → ${fmtRole(nextRole)}`
               : `Block #${idx} role reset to detected`);
    // Optimistic update.
    setState((s) => ({
      ...s,
      blocks: s.blocks.map((b) => (b.source_index === idx
        ? { ...b, effective_role: nextRole || b.detected_role, is_overridden: !!nextRole }
        : b)),
    }));
    setSavingIdx((set) => new Set(set).add(idx));
    try {
      const { data } = await api.patch(`/books/${bookId}/structure/${idx}`,
        { role: nextRole || null });
      applyPatchResponse(idx, data);
      onBookUpdated?.(data.book);
      if (nextRole) toast.success(`Set #${idx} → ${fmtRole(nextRole)}`);
      else toast.success(`Restored #${idx} to detected`);
    } catch (err) {
      toast.error(formatApiError(err));
      setState((s) => ({ ...s, blocks: prevBlocks })); // rollback
    } finally {
      setSavingIdx((set) => { const n = new Set(set); n.delete(idx); return n; });
    }
  }, [bookId, state.blocks, onBookUpdated, applyPatchResponse, history]);

  const resetAll = useCallback(async () => {
    try {
      history?.record?.("structure_reset", "Cleared all role overrides");
      const { data } = await api.post(`/books/${bookId}/structure/reset`);
      onBookUpdated?.(data.book);
      toast.success("Overrides cleared");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }, [bookId, onBookUpdated, load, history]);

  // iter-65 — per-block lock/unlock toggle. Locked blocks reject role
  // edits from the classifier, bulk apply, and manual dropdown. Uses
  // an optimistic update + rollback on failure so the click feels
  // instant even on slow networks.
  const toggleLock = useCallback(async (block) => {
    const idx = block.source_index;
    const next = !block.is_locked;
    setState((s) => ({
      ...s,
      blocks: s.blocks.map((b) => b.source_index === idx ? { ...b, is_locked: next } : b),
    }));
    try {
      await api.patch(`/books/${bookId}/structure/${idx}/lock`,
                          { locked: next });
      toast.success(next ? `Locked #${idx}` : `Unlocked #${idx}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setState((s) => ({
        ...s,
        blocks: s.blocks.map((b) => b.source_index === idx ? { ...b, is_locked: !next } : b),
      }));
    }
  }, [bookId]);

  // ---- Tier C · Selection + bulk actions ----------------------------
  /** Toggle-select a single block. Standard patterns:
   *   click                 → replace selection with this block
   *   ctrl/cmd + click      → toggle this block
   *   shift + click         → select range from lastAnchor to this block
   */
  const toggleSelect = useCallback((sourceIndex, event) => {
    const shift = event.shiftKey;
    const meta = event.ctrlKey || event.metaKey;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastAnchorRef.current != null) {
        const lo = Math.min(lastAnchorRef.current, sourceIndex);
        const hi = Math.max(lastAnchorRef.current, sourceIndex);
        for (const b of state.blocks) {
          if (b.source_index >= lo && b.source_index <= hi) next.add(b.source_index);
        }
      } else if (meta) {
        if (next.has(sourceIndex)) next.delete(sourceIndex);
        else next.add(sourceIndex);
        lastAnchorRef.current = sourceIndex;
      } else {
        next.clear();
        next.add(sourceIndex);
        lastAnchorRef.current = sourceIndex;
      }
      return next;
    });
  }, [state.blocks]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    lastAnchorRef.current = null;
  }, []);

  const applyBulk = useCallback(async (role) => {
    if (!selected.size) return;
    setBulkBusy(true);
    const updates = {};
    for (const idx of selected) updates[idx] = role || null;
    history?.record?.("structure_bulk_role",
      role ? `Bulk set ${selected.size} block${selected.size === 1 ? "" : "s"} → ${fmtRole(role)}`
           : `Bulk reset ${selected.size} block${selected.size === 1 ? "" : "s"} to detected`);
    try {
      const { data } = await api.patch(`/books/${bookId}/structure/roles`, { updates });
      onBookUpdated?.(data.book);
      toast.success(role
        ? `Set ${data.applied} block${data.applied === 1 ? "" : "s"} → ${fmtRole(role)}`
        : `Restored ${data.applied} block${data.applied === 1 ? "" : "s"} to detected`);
      clearSelection();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkBusy(false); }
  }, [bookId, selected, onBookUpdated, clearSelection, load, history]);

  /** Convenience: split — promote a body block to a chapter title.
   *  Merge into previous — demote a chapter_title back to body_paragraph.
   *  Both are just role changes; wrapping them so the UI reads naturally.
   */
  const splitChapterAt = useCallback((block) => {
    setBlockRole(block, "chapter_title");
  }, [setBlockRole]);
  const mergeIntoPrevious = useCallback((block) => {
    setBlockRole(block, "body_paragraph");
  }, [setBlockRole]);

  /** iter-68 — delete a single block, or a whole chapter when the target
   *  is a chapter heading (backend cascades). Confirmed via native
   *  window.confirm because the structural editor already runs inside a
   *  modal — nesting AlertDialog here would layer three modals deep.
   */
  const deleteBlock = useCallback(async (block) => {
    const isChapterHead = block.effective_role === "chapter_title"
                        || block.effective_role === "chapter_label";
    const label = isChapterHead
      ? `Delete this entire chapter (heading + every block underneath)?\n\nThis can't be undone from here — export a copy first if you want a backup.`
      : `Delete this block?`;
    if (!window.confirm(label)) return;
    setSavingIdx((prev) => new Set([...prev, block.source_index]));
    history?.record?.("structure_delete",
      isChapterHead ? `Deleted chapter starting at block ${block.source_index}`
                    : `Deleted block ${block.source_index}`);
    try {
      const { data } = await api.delete(`/books/${bookId}/structure/${block.source_index}`);
      onBookUpdated?.(data.book);
      toast.success(data.cascade_chapter
        ? `Chapter removed (${data.removed_source_indices.length} block${data.removed_source_indices.length === 1 ? "" : "s"})`
        : "Block removed");
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingIdx((prev) => {
        const next = new Set(prev);
        next.delete(block.source_index);
        return next;
      });
    }
  }, [bookId, onBookUpdated, clearSelection, load, history]);

  // ---- Drag & Drop handlers -----------------------------------------
  const onBlockDragStart = useCallback((block) => (e) => {
    e.dataTransfer.setData(DND_MIME, String(block.source_index));
    e.dataTransfer.effectAllowed = "move";
    setDragging({ source_index: block.source_index });
  }, []);
  const onBlockDragEnd = useCallback(() => {
    setDragging(null);
    setHoverRole(null);
  }, []);
  const onRoleDragEnter = useCallback((role) => (e) => {
    e.preventDefault();
    setHoverRole(role);
  }, []);
  const onRoleDragOver = useCallback((e) => {
    if (e.dataTransfer.types.includes(DND_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    }
  }, []);
  const onRoleDrop = useCallback((role) => (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(DND_MIME);
    setHoverRole(null);
    setDragging(null);
    if (!raw) return;
    const idx = Number(raw);
    const block = state.blocks.find((b) => b.source_index === idx);
    if (!block) return;
    // Restore-to-detected when dropped on the block's current detected role.
    if (block.detected_role === role) {
      setBlockRole(block, null);
    } else {
      setBlockRole(block, role);
    }
  }, [state.blocks, setBlockRole]);

  if (state.loading) {
    return (
      <div className="mt-6 border border-[color:var(--border)] bg-white p-4 text-sm text-[color:var(--text-muted)]"
           data-testid="structure-loading">Loading structure…</div>
    );
  }
  if (!state.has_structure) {
    return (
      <div className="mt-6 border border-[color:var(--border)] bg-white p-4 text-sm text-[color:var(--text-muted)]"
           data-testid="structure-empty">
        Upload a DOCX or TXT manuscript to enable the block-level structural editor.
      </div>
    );
  }

  const overrideCount = Object.keys(state.overrides || {}).length;
  const visibleGroups = expandedList ? groups : groups.slice(0, 3);
  const draggingBlock = dragging
    ? state.blocks.find((b) => b.source_index === dragging.source_index)
    : null;

  return (
    <div className="mt-6 border border-[color:var(--border)] bg-white" data-testid="structure-panel">
      {/* --- Header ------------------------------------------------- */}
      <div className="border-b border-[color:var(--border)] px-5 py-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Structural editor</p>
          <h3 className="text-lg font-semibold mt-1">Reclassify blocks</h3>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            {state.blocks.length.toLocaleString()} source blocks · {overrideCount} manual override{overrideCount === 1 ? "" : "s"} · autosaves · drag to a role chip
          </p>
        </div>
        <div className="flex items-center gap-2">
          {overrideCount > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="btn-ghost text-sm inline-flex items-center gap-1"
                        data-testid="structure-reset-all-trigger">
                  <RotateCcw className="w-4 h-4"/> Reset all
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="structure-reset-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all overrides?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This restores every block to the classifier's detected role.
                    Your {overrideCount} manual override{overrideCount === 1 ? "" : "s"} will be discarded.
                    The preview and exported PDF will re-render.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="structure-reset-cancel">Keep overrides</AlertDialogCancel>
                  <AlertDialogAction data-testid="structure-reset-confirm" onClick={resetAll}>
                    Clear all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* --- Bulk selection bar (Tier C) ---------------------------- */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-30 bg-amber-50 border-b border-amber-300 px-4 py-2 flex items-center gap-3 flex-wrap"
             data-testid="structure-bulk-bar"
             data-selected-count={selected.size}>
          <span className="text-xs font-medium text-amber-900">
            {selected.size} block{selected.size === 1 ? "" : "s"} selected
          </span>
          <div className="inline-flex items-center gap-2 flex-1 flex-wrap">
            <label className="text-xs flex items-center gap-1">
              <span className="uppercase tracking-widest text-amber-800">Apply role</span>
              <select
                onChange={(e) => { if (e.target.value) applyBulk(e.target.value); e.target.value = ""; }}
                disabled={bulkBusy}
                defaultValue=""
                className="text-xs border border-amber-500 bg-white px-2 py-1"
                data-testid="structure-bulk-role-select"
              >
                <option value="" disabled>Pick a role…</option>
                {(state.role_choices || []).map((r) => (
                  <option key={r} value={r}>{fmtRole(r)}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() => applyBulk(null)}
              disabled={bulkBusy}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              title="Restore detected role for every selected block"
              data-testid="structure-bulk-restore"
            >
              <Wand2 className="w-3.5 h-3.5"/> Restore detected
            </button>
          </div>
          {bulkBusy && <span className="text-[10px] uppercase tracking-widest text-amber-800">Saving…</span>}
          <button onClick={clearSelection}
                  className="btn-ghost text-xs inline-flex items-center gap-1 ml-auto"
                  data-testid="structure-bulk-clear">
            <XCircle className="w-3.5 h-3.5"/> Clear selection
          </button>
        </div>
      )}

      {/* --- Role tray (Tier B) ------------------------------------- */}
      <div className={`sticky top-0 z-20 bg-[color:var(--canvas)] border-b border-[color:var(--border)] px-3 py-2 ${dragging ? "shadow-inner ring-1 ring-amber-300" : ""}`}
           data-testid="structure-role-tray">
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] px-2 pb-1">
          {dragging
            ? `Drop block #${dragging.source_index} on a role · currently ${fmtRole(draggingBlock?.effective_role)}`
            : "Drag any block onto a role chip to reclassify it"}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(state.role_choices || []).map((role) => {
            const isHover = hoverRole === role;
            const isCurrentOfDragging = draggingBlock?.effective_role === role;
            return (
              <button
                key={role}
                type="button"
                onDragEnter={onRoleDragEnter(role)}
                onDragOver={onRoleDragOver}
                onDragLeave={() => setHoverRole((r) => (r === role ? null : r))}
                onDrop={onRoleDrop(role)}
                onClick={() => {
                  if (!draggingBlock) return;
                  // Also allow a keyboard/click flow: after clicking a block's
                  // GripVertical, clicking a chip applies the role. Nice
                  // fallback for touch/no-DnD environments.
                }}
                className={
                  "text-xs px-2.5 py-1 rounded-full border transition-colors " +
                  (isHover
                    ? "bg-amber-100 border-amber-500 text-amber-900 ring-2 ring-amber-400"
                    : isCurrentOfDragging
                      ? "bg-[color:var(--canvas)] border-[color:var(--border)] text-[color:var(--text-muted)] opacity-60"
                      : "bg-white border-[color:var(--border)] hover:border-[color:var(--text-muted)]")
                }
                data-testid={`structure-role-chip-${role}`}
                data-drop-hover={isHover ? "true" : "false"}
              >
                {fmtRole(role)}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- Groups -------------------------------------------------- */}
      <div className="max-h-[70vh] overflow-y-auto" data-testid="structure-groups">
        {visibleGroups.map((g) => (
          <BlockGroup
            key={g.key} group={g}
            expanded={!!expanded[g.key] || autoExpandRef.current.has(g.key)}
            setExpanded={(v) => setExpanded((prev) => ({ ...prev, [g.key]: v }))}
            roleChoices={state.role_choices}
            savingIdx={savingIdx}
            onChangeRole={setBlockRole}
            onToggleLock={toggleLock}
            onBlockDragStart={onBlockDragStart}
            onBlockDragEnd={onBlockDragEnd}
            draggingIdx={dragging?.source_index}
            selected={selected}
            onToggleSelect={toggleSelect}
            onSplitHere={splitChapterAt}
            onMergeIntoPrevious={mergeIntoPrevious}
            onDeleteBlock={deleteBlock}
          />
        ))}
        {groups.length > visibleGroups.length && (
          <div className="px-5 py-3 border-t border-[color:var(--border)] text-center">
            <button onClick={() => setExpandedList(true)}
                    className="btn-ghost text-sm" data-testid="structure-show-all">
              Show {groups.length - visibleGroups.length} more chapters / sections
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BlockGroup({ group, expanded, setExpanded, roleChoices, savingIdx,
                      onChangeRole, onToggleLock, onBlockDragStart, onBlockDragEnd, draggingIdx,
                      selected, onToggleSelect, onSplitHere, onMergeIntoPrevious,
                      onDeleteBlock }) {
  const overriddenCount = group.blocks.filter((b) => b.is_overridden).length;
  const lockedCount = group.blocks.filter((b) => b.is_locked).length;
  const isUnresolved = group.kind === "unresolved";
  return (
    <div className="border-b border-[color:var(--border)] last:border-b-0"
         data-testid={`structure-group-${group.key}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-[color:var(--canvas)]"
        data-testid={`structure-group-toggle-${group.key}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
          {isUnresolved && <AlertCircle className="w-4 h-4 text-amber-600"/>}
          <span className="font-medium truncate">{group.label}</span>
        </div>
        <div className="text-xs text-[color:var(--text-muted)] shrink-0">
          {group.blocks.length} block{group.blocks.length === 1 ? "" : "s"}
          {overriddenCount > 0 && ` · ${overriddenCount} overridden`}
          {lockedCount > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 text-[color:var(--primary)]">
              <Lock className="w-3 h-3" strokeWidth={1.75}/> {lockedCount}
            </span>
          )}
        </div>
      </button>
      {expanded && (
        <ul className="divide-y divide-[color:var(--border)]">
          {group.blocks.map((b) => (
            <BlockRow key={b.id} block={b} roleChoices={roleChoices}
                      saving={savingIdx.has(b.source_index)}
                      onChangeRole={onChangeRole}
                      onToggleLock={onToggleLock}
                      onBlockDragStart={onBlockDragStart}
                      onBlockDragEnd={onBlockDragEnd}
                      isDragging={draggingIdx === b.source_index}
                      isSelected={selected.has(b.source_index)}
                      onToggleSelect={onToggleSelect}
                      onSplitHere={onSplitHere}
                      onMergeIntoPrevious={onMergeIntoPrevious}
                      onDeleteBlock={onDeleteBlock} />
          ))}
        </ul>
      )}
    </div>
  );
}

function BlockRow({ block, roleChoices, saving, onChangeRole, onToggleLock,
                    onBlockDragStart, onBlockDragEnd, isDragging,
                    isSelected, onToggleSelect, onSplitHere, onMergeIntoPrevious,
                    onDeleteBlock }) {
  const [value, setValue] = useState(block.effective_role || "");
  useEffect(() => { setValue(block.effective_role || ""); }, [block.effective_role]);
  const detected = block.detected_role;
  const overridden = block.is_overridden;
  const locked = !!block.is_locked;
  // A block "heads a chapter" if its effective role is what the boundary
  // engine considers a chapter opener. In practice that's either the
  // curated ``chapter_title`` role (which the editor lets users promote to)
  // or the raw ``chapter_label`` role that the classifier assigns to
  // headings like "Chapter 3". Both need the Merge affordance.
  const isChapterHead = block.effective_role === "chapter_title"
                        || block.effective_role === "chapter_label";
  return (
    <li
      className={`px-5 py-3 flex items-start gap-3 ${
        isDragging ? "opacity-40" : ""
      } ${isSelected ? "bg-amber-50/70 border-l-2 border-amber-500" : "hover:bg-[color:var(--canvas)]/60"} ${
        locked ? "bg-[color:var(--surface)]/40" : ""
      }`}
      draggable={!locked}
      onDragStart={locked ? undefined : onBlockDragStart(block)}
      onDragEnd={onBlockDragEnd}
      data-testid={`structure-block-${block.source_index}`}
      data-source-index={block.source_index}
      data-effective-role={block.effective_role}
      data-overridden={overridden ? "true" : "false"}
      data-selected={isSelected ? "true" : "false"}
      data-locked={locked ? "true" : "false"}
    >
      <button
        type="button"
        onClick={(e) => onToggleSelect(block.source_index, e)}
        className="pt-1 shrink-0 text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
        title="Click to select · Shift-click to select range · Ctrl/Cmd-click to toggle"
        data-testid={`structure-block-checkbox-${block.source_index}`}
      >
        {isSelected
          ? <CheckSquare className="w-4 h-4 text-amber-600"/>
          : <Square className="w-4 h-4"/>}
      </button>
      <div className="pt-1 shrink-0 flex items-center gap-1">
        <GripVertical className="w-4 h-4 text-[color:var(--text-muted)] cursor-grab active:cursor-grabbing"
                       title="Drag onto a role chip"
                       data-testid={`structure-block-handle-${block.source_index}`} />
        <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] w-10">
          #{block.source_index}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">{trim(block.text) || <span className="italic text-[color:var(--text-muted)]">(empty)</span>}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[color:var(--text-muted)]">
          <span>Detected: <strong className="text-[color:var(--text)]">{fmtRole(detected)}</strong></span>
          {block.confidence != null && <span>· conf {(block.confidence * 100).toFixed(0)}%</span>}
          {block.style_name && <span>· style {block.style_name}</span>}
          {block.is_bold && <span>· bold</span>}
          {block.is_italic && <span>· italic</span>}
          {overridden && <span className="text-amber-600">· overridden</span>}
          {locked && (
            <span className="inline-flex items-center gap-1 text-[color:var(--primary)]"
                   data-testid={`structure-block-locked-${block.source_index}`}>
              · <Lock className="w-3 h-3" strokeWidth={2}/> locked
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {/* iter-65 — Lock / Unlock toggle. Always visible so users
            can confirm & pin any TOC entry. */}
        <button
          onClick={() => onToggleLock(block)}
          disabled={saving}
          className={`text-xs inline-flex items-center gap-1 border px-2 py-1 rounded transition-colors ${
            locked
              ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]"
              : "btn-ghost"
          }`}
          title={locked
            ? "Unlock this block — allows role changes and drag reorder"
            : "Lock this block — pins the confirmed chapter/section in place"}
          data-testid={`structure-lock-toggle-${block.source_index}`}
        >
          {locked
            ? <><Lock className="w-3 h-3" strokeWidth={2}/> Locked</>
            : <><Unlock className="w-3 h-3" strokeWidth={2}/> Lock</>}
        </button>
        {/* Tier C split/merge shortcut buttons (disabled while locked) */}
        {isChapterHead ? (
          <button
            onClick={() => onMergeIntoPrevious(block)}
            disabled={saving || locked}
            className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
            title={locked
              ? "Unlock first to merge this chapter"
              : "Demote this chapter title back to body — merges into the previous chapter"}
            data-testid={`structure-merge-${block.source_index}`}
          >
            <Combine className="w-3 h-3"/> Merge
          </button>
        ) : (
          <button
            onClick={() => onSplitHere(block)}
            disabled={saving || locked}
            className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
            title={locked
              ? "Unlock first to split"
              : "Promote to chapter title — starts a new chapter here"}
            data-testid={`structure-split-${block.source_index}`}
          >
            <SplitSquareVertical className="w-3 h-3"/> Split
          </button>
        )}
        <select
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            setValue(v);
            if (v === detected) onChangeRole(block, null);
            else onChangeRole(block, v);
          }}
          disabled={saving || locked}
          className={`text-xs border border-[color:var(--border)] bg-white px-2 py-1 ${overridden ? "ring-1 ring-amber-400" : ""} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid={`structure-role-select-${block.source_index}`}
        >
          {detected && !roleChoices.includes(detected) && (
            <option value={detected}>{fmtRole(detected)} (detected)</option>
          )}
          {roleChoices.map((r) => (
            <option key={r} value={r}>{fmtRole(r)}{r === detected ? " (detected)" : ""}</option>
          ))}
        </select>
        {overridden && (
          <button
            onClick={() => { setValue(detected || ""); onChangeRole(block, null); }}
            disabled={saving}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            title="Restore to detected role"
            data-testid={`structure-restore-${block.source_index}`}
          >
            <Wand2 className="w-3 h-3"/> Restore
          </button>
        )}
        {/* iter-68 — per-block delete. On a chapter heading the backend
            cascades and removes every block under this chapter. */}
        {onDeleteBlock && (
          <button
            onClick={() => onDeleteBlock(block)}
            disabled={saving || locked}
            className="btn-ghost text-xs inline-flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-40"
            title={locked
              ? "Unlock this block first to delete it"
              : (isChapterHead
                  ? "Delete this whole chapter (heading + all blocks underneath)"
                  : "Delete this block")}
            data-testid={`structure-delete-${block.source_index}`}
          >
            <Trash2 className="w-3 h-3"/> Delete
          </button>
        )}
        {saving && <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Saving…</span>}
      </div>
    </li>
  );
}
